import { paiseToDisplay, parseCurrencyType } from '@/lib/currency'
import { isUnavailable, type AnalyticsUnavailable } from '@/api/map/pnl'
import { unstopped, type Account, type ActivityEvent, type ActivityKind, type Role } from '@/data/types'
import { STOP_COPY } from '@/lib/status'

/** analytics-service Money — minor units as string. */
export type MoneyMinor = string

export interface AnalyticsActivityEntry {
  id: string
  occurredAt: string
  action: string
  actorId: string
  actorName?: string
  subjectId?: string | null
  subjectName?: string | null
  amountMinor?: string | null
  detail?: Record<string, unknown>
}

export interface AnalyticsActivityResponse {
  asOf?: string
  martBuiltAt?: string | null
  martStale?: boolean
  nextCursor?: string | null
  rows: AnalyticsActivityEntry[]
}

export type ActivityRowView = ActivityEvent & {
  actor: Account | null
  subject: Account | null
  /** Raw OpenAPI action string (e.g. credit.given). */
  action: string
}

export type ActivityReport = {
  asOf?: string
  martStale?: boolean
  /** Set when live analytics answered 503 Unavailable (slice a7). */
  unavailable?: AnalyticsUnavailable
  rows: ActivityRowView[]
  nextCursor?: string | null
  /**
   * Live ActivityEntry has no device/IP fields (and must never grow surveillance
   * ones). Fixture rows may still carry provenance.
   */
  provenanceFromAudit: boolean
}

function stubAccount(id: string, name: string, role: Role = 'master'): Account {
  return {
    id,
    username: name || id.slice(0, 8),
    displayName: name || id.slice(0, 8),
    role,
    parentId: null,
    path: [],
    status: 'active',
    createdAt: '',
    lastActiveAt: null,
    creditLimit: 0,
    balance: 0,
    distributedBalance: 0,
    exposure: 0,
    settlement: 0,
    share: { sportsbook: 0, casino: 0 },
    commission: { fancy: 0, bookmaker: 0, matchOdds: 0 },
    betLimits: { min: 0, max: 0 },
    games: {
      casino: true,
      cardGame: true,
      liveGame: true,
      sportsbook: true,
    },
    childCount: 0,
    memberCount: 0,
    flags: [],
    // An audit/reporting stub, not a hierarchy row: nothing here can be stopped,
    // and no backend behind it could enforce a control if one were offered.
    ...unstopped('active', STOP_COPY.notWiredLive),
  }
}

/** Map OpenAPI `action` → console ActivityKind for filters. */
export function kindFromAction(action: string): ActivityKind {
  const a = action.trim().toLowerCase()
  if (a.startsWith('credit.') || a.includes('transfer')) return 'transfer'
  if (a.endsWith('.created') || a.includes('account.created')) return 'account-created'
  if (a.includes('limit')) return 'limit-changed'
  if (a.includes('status') || a.includes('suspend') || a.includes('reactivat')) {
    return 'status-changed'
  }
  if (a.includes('login') || a.includes('sign-in') || a.includes('signin')) return 'login'
  if (a.includes('password')) return 'password-reset'
  if (
    a.includes('settlement') ||
    a.includes('result.entered') ||
    a.includes('rolled_back') ||
    a.includes('rollback')
  ) {
    return 'settlement'
  }
  return 'settlement'
}

export function summaryFromEntry(row: AnalyticsActivityEntry): string {
  const subject = row.subjectName?.trim() || (row.subjectId ? row.subjectId.slice(0, 8) : 'account')
  const action = row.action.trim().toLowerCase()
  switch (action) {
    case 'credit.given':
      return `Gave credit to ${subject}`
    case 'credit.taken':
      return `Took credit from ${subject}`
    case 'agent.created':
      return `Created agent ${subject}`
    case 'member.created':
      return `Created member ${subject}`
    case 'result.entered':
      return `Entered result for ${subject}`
    case 'settlement.rolled_back':
      return `Rolled back settlement for ${subject}`
    default:
      return row.action
  }
}

/** Flatten detail object for display — never invent device/IP. */
export function detailFromEntry(detail: Record<string, unknown> | undefined): string | undefined {
  if (!detail || typeof detail !== 'object') return undefined
  const parts: string[] = []
  for (const [key, value] of Object.entries(detail)) {
    const k = key.toLowerCase()
    // OpenAPI forbids member device/IP/behavioural data — skip if a rogue field appears.
    if (
      k === 'ip' ||
      k === 'device' ||
      k.includes('fingerprint') ||
      k.includes('useragent') ||
      k.includes('user_agent')
    ) {
      continue
    }
    if (value === null || value === undefined) continue
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      parts.push(`${key}: ${value}`)
    }
  }
  return parts.length > 0 ? parts.join(' · ') : undefined
}

export function toActivityRowView(
  row: AnalyticsActivityEntry,
  currency?: string,
): ActivityRowView {
  const actorName = row.actorName?.trim() || row.actorId.slice(0, 8)
  const subjectId = row.subjectId ?? ''
  const subjectName = row.subjectName?.trim() || (subjectId ? subjectId.slice(0, 8) : '')
  const amount =
    row.amountMinor === null || row.amountMinor === undefined
      ? undefined
      : paiseToDisplay(row.amountMinor, parseCurrencyType(currency))

  return {
    id: row.id,
    at: row.occurredAt,
    kind: kindFromAction(row.action),
    action: row.action,
    actorId: row.actorId,
    subjectId,
    summary: summaryFromEntry(row),
    detail: detailFromEntry(row.detail),
    amount,
    actor: stubAccount(row.actorId, actorName),
    subject: subjectId ? stubAccount(subjectId, subjectName, 'player') : null,
  }
}

export function toActivityReport(
  live: AnalyticsActivityResponse,
  currency?: string,
): ActivityReport {
  return {
    asOf: live.asOf,
    martStale: Boolean(live.martStale),
    rows: (live.rows ?? []).map((row) => toActivityRowView(row, currency)),
    nextCursor: live.nextCursor ?? null,
    provenanceFromAudit: true,
  }
}

export function unavailableActivityReport(unavailable: AnalyticsUnavailable): ActivityReport {
  return {
    unavailable,
    rows: [],
    nextCursor: null,
    provenanceFromAudit: true,
  }
}

/** Fixture listActivity → report. */
export function fromFixtureRows(
  rows: (ActivityEvent & { actor: Account | null; subject: Account | null })[],
): ActivityReport {
  return {
    asOf: new Date().toISOString(),
    martStale: false,
    rows: rows.map((r) => ({ ...r, action: r.kind })),
    nextCursor: null,
    provenanceFromAudit: false,
  }
}

export { isUnavailable }
export type { AnalyticsUnavailable }
